/**
 * Login Page - Entra External ID auth provider selection
 */

import { AlertCircle, ClipboardCheck, Eye, LineChart, ShieldCheck } from 'lucide-react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { AuthBrandImage } from '../../components/auth/AuthBrandImage';
import { EntraSignInButton } from '../../components/auth/ProviderButtons';
import { slideLeft, slideUp } from '../../styles/animations';
import { media } from '../../styles/media';

const ERROR_MESSAGES: Record<string, string> = {
  login_not_activated:
    'Your account has not been activated for login. Please contact your administrator.',
  account_inactive: 'Your account has been deactivated. Please contact your administrator.',
  // Sign-in disabled by the organization (e.g. departed/terminated member) — not a fault.
  account_disabled: 'Your account has been disabled. Contact your organization administrator.',
  // Transient identity-provider fault — worth retrying.
  idp_unavailable:
    'The sign-in service is temporarily unavailable. Please try again in a few minutes.',
  no_organization:
    'Your account is not associated with any organization. Please contact your administrator.',
  auth_failed: 'Sign in failed. Please try again or contact your administrator.',
};

/* ── Layout ── */

const Container = styled.div`
  display: flex;
  min-height: 100vh;

  ${media.tablet} {
    flex-direction: column;
  }
`;

const FormColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.backgrounds.page};
  padding: ${({ theme }) => theme.spacing[8]};
`;

const MarketingColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.gradients.sidebar};
  color: ${({ theme }) => theme.colors.text.white};
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[10]};
  animation: ${slideLeft} 0.7s ease-out;

  ${media.tablet} {
    display: none;
  }
`;

const AnimatedDiv = styled.div<{ $delay?: number }>`
  animation: ${slideUp} 0.5s ease-out both;
  animation-delay: ${({ $delay }) => ($delay ?? 0) * 100}ms;
`;

const BrandImage = styled(AuthBrandImage)`
  margin-bottom: ${({ theme }) => theme.spacing[8]};
`;

const FormCard = styled.div`
  width: 100%;
  max-width: 420px;
`;

const FormHeading = styled.h1`
  font-size: 1.625rem;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const FormSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  margin-bottom: ${({ theme }) => theme.spacing[8]};
`;

const FormBox = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius['2xl']};
  padding: ${({ theme }) => theme.spacing[8]};
  box-shadow: ${({ theme }) => theme.shadows.xl};
`;

const ErrorAlert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.status.error.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

/* ── Marketing panel ── */

const MarketingInner = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const MarketingLogo = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing[6]};

  svg {
    width: 28px;
    height: 28px;
  }
`;

const MarketingTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.white};
`;

const MarketingSubtext = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  opacity: 0.8;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  margin-bottom: ${({ theme }) => theme.spacing[10]};
  max-width: 380px;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};

  ${media.tablet} {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing[4]};
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }
`;

const FeatureIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing[3]};

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FeatureTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  color: ${({ theme }) => theme.colors.text.white};
`;

const FeatureDesc = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  opacity: 0.7;
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
`;

const MarketingFooter = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  opacity: 0.5;
  padding-top: ${({ theme }) => theme.spacing[8]};
`;

const FEATURES = [
  { icon: <ClipboardCheck />, title: 'Clinical decision support systems', desc: 'Real-time guidance for BCBAs that allows for consistent, informed decision-making' },
  { icon: <ShieldCheck />, title: 'HIPAA compliant', desc: 'Enterprise-grade security for protected health information' },
  { icon: <LineChart />, title: 'Better outcomes', desc: 'Ensure consistent, high-quality care across your caseload' },
  { icon: <Eye />, title: 'Full visibility, top to bottom', desc: 'Every case is trackable from RBT to CEO — transparency across every layer of the organization' },
];

export const LoginPage = () => {
  const location = useLocation();
  const authErrorCode = (location.state as { authError?: string } | null)?.authError;
  const errorMessage = authErrorCode
    ? (ERROR_MESSAGES[authErrorCode] ?? ERROR_MESSAGES.auth_failed)
    : null;

  // Show the failed-sign-in message once, then drop it from the history entry so a
  // later in-SPA back/forward navigation doesn't resurface a stale "Signing failed".
  // (replaceState doesn't re-render, so the message stays visible for this view.)
  useEffect(() => {
    if (authErrorCode) {
      window.history.replaceState({}, '', location.pathname);
    }
  }, [authErrorCode, location.pathname]);

  return (
    <Container>
      <FormColumn>
        <FormCard>
          <AnimatedDiv $delay={0}>
            <BrandImage />
          </AnimatedDiv>

          <AnimatedDiv $delay={1}>
            <FormHeading>Sign in to your account</FormHeading>
            <FormSubtitle></FormSubtitle>
          </AnimatedDiv>

          <AnimatedDiv $delay={2}>
            <FormBox>
              {errorMessage && (
                <ErrorAlert>
                  <AlertCircle />
                  <span>{errorMessage}</span>
                </ErrorAlert>
              )}
              <EntraSignInButton />
            </FormBox>
          </AnimatedDiv>
        </FormCard>
      </FormColumn>

      <MarketingColumn>
        <MarketingInner>
          <MarketingLogo>
            <LineChart />
          </MarketingLogo>

          <MarketingTitle>
            Designed for agencies.
            <br />
            Built for clinicians by clinicians.
          </MarketingTitle>

          <MarketingSubtext>
            Part project management, part clinical expertise — what to do next, and how to do it
            right.
          </MarketingSubtext>

          <FeatureGrid>
            {FEATURES.map((f) => (
              <FeatureCard key={f.title}>
                <FeatureIcon>{f.icon}</FeatureIcon>
                <FeatureTitle>{f.title}</FeatureTitle>
                <FeatureDesc>{f.desc}</FeatureDesc>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </MarketingInner>

        <MarketingFooter>&copy; 2026 Upskill ABA Solutions, Inc. All rights reserved.</MarketingFooter>
      </MarketingColumn>
    </Container>
  );
};

export default LoginPage;
